import axios from "../../axios";
import { parseProgramOverview, ProgramOverviewResponse } from "../programs/parsePrograms";
import type { Asset, AssetTarget, TargetResource, AssetGlobalContext } from "../companies";

const BASE_URL = "/v2/programs";

// ---------------------------------------------------------------------------
// Program Asset Linking Types
// ---------------------------------------------------------------------------

/**
 * Target with filtered resources for program scope
 */
export type LinkedTarget = {
  target_id: number;
  target_type: AssetTarget["target_type"];
  target_value: string;
  environment: AssetTarget["environment"];
  validation_state: AssetTarget["validation_state"];
  verification_state: AssetTarget["verification_state"];
  notes?: string | null;
  tech_stack?: string[] | null;
  deleted_at?: string | null;
  resources: Array<{
    resource_id: number;
    value: string;
    resource_type: TargetResource["resource_type"];
    status: TargetResource["status"];
    http_method?: string | null;
    notes?: string | null;
    discovery_method?: "MANUAL" | "IMPORT" | "PENTESTER" | "AI_DISCOVERY";
    deleted_at?: string | null;
    created_by?: number | null;
    creator?: {
      display_name: string;
      username: string;
      pfp: string | any;
    } | null;
  }>;
  resource_count: number;
};

/**
 * Asset linked to program with target/resource selection
 */
export type LinkedAsset = {
  program_asset_id: number;
  asset_id: number;
  company_id: number;
  name: string;
  description?: string | null;
  type: Asset["type"];
  status: Asset["status"];
  criticality: Asset["criticality"];
  data_sensitivity?: Asset["data_sensitivity"];
  owner_label?: string | null;
  global_context?: AssetGlobalContext | null;
  auth_required?: boolean;
  access_surface_type?: string | null;
  role_context?: string | null;
  supported_auth_methods?: string | null;
  scope_state: "IN_SCOPE" | "OUT_OF_SCOPE" | "INTERNAL_ONLY" | "PENDING";
  notes?: string | null;
  include_all_targets: boolean;
  include_all_resources: boolean;
  target_ids?: number[] | null;
  resource_ids?: number[] | null;
  targets: LinkedTarget[];
  target_count: number;
  total_target_count: number;
};

/**
 * Input for linking asset to program
 */
export type LinkedAssetInput = {
  asset_id: number;
  scope_state?: "IN_SCOPE" | "OUT_OF_SCOPE" | "INTERNAL_ONLY" | "PENDING";
  notes?: string;
  target_ids?: number[] | null;
  resource_ids?: number[] | null;
  include_all_targets?: boolean;
  include_all_resources?: boolean;
};

/**
 * Legacy response type for backward compatibility
 */
export type ProgramAssetsResponse = {
  program_id: number;
  total_assets: number;
  assets: Asset[];
};

/**
 * Enhanced response type with full linking details
 */
export type ProgramLinkedAssetsResponse = {
  program_id: number;
  total_assets: number;
  linked_assets: LinkedAsset[];
};

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

export const getProgramOverview = async (
  programId: number
): Promise<ProgramOverviewResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/get-program-overview/${programId}`);
    const parsedData: ProgramOverviewResponse = parseProgramOverview(response.data);
    return parsedData;
  } catch (error) {
    console.error("Error fetching program overview:", error);
    throw error;
  }
};

/**
 * Get program assets with full linking details (targets, resources)
 */
export const getProgramLinkedAssets = async (
  programId: number
): Promise<ProgramLinkedAssetsResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/${programId}/assets`);
    const payload = response.data?.data;

    if (!payload) {
      return {
        program_id: programId,
        total_assets: 0,
        linked_assets: []
      };
    }

    return {
      program_id: payload.program_id ?? programId,
      total_assets: payload.total_assets ?? 0,
      linked_assets: payload.linked_assets ?? []
    };
  } catch (error) {
    console.error("Error fetching program linked assets:", error);
    throw error;
  }
};

/**
 * Legacy function for backward compatibility
 */
export const getProgramAssets = async (
  programId: number
): Promise<ProgramAssetsResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/${programId}/assets`);
    const payload = response.data?.data;

    if (!payload) {
      return {
        program_id: programId,
        total_assets: 0,
        assets: []
      };
    }

    // Transform linked_assets to simple assets for legacy compatibility
    const linkedAssets = payload.linked_assets || [];
    const assets: Asset[] = linkedAssets.map((la: LinkedAsset) => ({
      asset_id: la.asset_id,
      company_id: la.company_id,
      name: la.name,
      description: la.description,
      type: la.type,
      status: la.status,
      criticality: la.criticality,
      data_sensitivity: la.data_sensitivity
    }));

    return {
      program_id: payload.program_id ?? programId,
      total_assets: payload.total_assets ?? assets.length,
      assets
    };
  } catch (error) {
    console.error("Error fetching program assets:", error);
    throw error;
  }
};

/**
 * Update program assets with enhanced linking (targets, resources)
 */
export const updateProgramLinkedAssets = async (
  programId: number,
  linkedAssets: LinkedAssetInput[]
): Promise<{ success: boolean; linked_count: number }> => {
  try {
    const response = await axios.put(`${BASE_URL}/${programId}/assets`, {
      linked_assets: linkedAssets
    });
    return {
      success: true,
      linked_count: response.data?.data?.linked_count ?? linkedAssets.length
    };
  } catch (error) {
    console.error("Error updating program linked assets:", error);
    throw error;
  }
};

/**
 * Legacy function: Update program assets (simple asset IDs only)
 */
export const updateProgramAssets = async (
  programId: number,
  assetIds: number[]
): Promise<boolean> => {
  try {
    await axios.put(`${BASE_URL}/${programId}/assets`, { asset_ids: assetIds });
    return true;
  } catch (error) {
    console.error("Error updating program assets:", error);
    throw error;
  }
};

